import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, RotateCw, X, Route, Navigation2, Hand, Copy, Check, Crosshair, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '../../../../components/common/Button';
import { Input } from '../../../../components/common/FormFields';
import { RouteMarker } from './markers/RouteMarker';
import { DirectionsRenderer } from '@react-google-maps/api';
import { LocationInfo } from './LocationInfo';
import { getNearestAddress } from '../../../../utils/poiHelpers';
import { logError, logInfo } from '../../../../utils/logger';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../config/firebase';
import { useSurvey } from '../../../../contexts/SurveyContext';

export const RoutePlanner = ({ map, onClose, editingRoute = null, className = '' }) => {
  const { activeSurvey } = useSurvey();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [selectionMode, setSelectionMode] = useState(true);
  const [points, setPoints] = useState([]);
  const [directions, setDirections] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [routeName, setRouteName] = useState(editingRoute?.name || '');
  const [routeColor, setRouteColor] = useState(editingRoute?.color || '#3B82F6');
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [copied, setCopied] = useState(false);

  // Initialize with editing route if provided
  useEffect(() => {
    if (editingRoute && editingRoute.trackPoints) {
      const routePoints = editingRoute.trackPoints.map(point => ({
        lat: point.latitude,
        lng: point.longitude
      }));
      setPoints(routePoints);
      calculateRoute(routePoints);
    }
  }, [editingRoute]);

  // Initialize DirectionsRenderer
  useEffect(() => {
    if (map) {
      const renderer = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        preserveViewport: true,
        map: map
      });
      setDirectionsRenderer(renderer);
      return () => renderer.setMap(null);
    }
  }, [map]);

  // Update directions on renderer
  useEffect(() => {
    if (directionsRenderer && directions) {
      directionsRenderer.setDirections(directions);
    }
  }, [directionsRenderer, directions]);

  const handleMapClick = useCallback(async (e) => {
    if (!selectionMode) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const address = await getNearestAddress(lat, lng);
    
    setPoints(prev => [...prev, { lat, lng, address }]);
  }, [selectionMode]);

  // Add click listener to map
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener('click', handleMapClick);
    return () => google.maps.event.removeListener(listener);
  }, [map, handleMapClick]);

  const calculateRoute = useCallback(async (routePoints = points) => {
    if (routePoints.length < 2) return;

    setCalculating(true);
    setError('');

    try {
      const origin = routePoints[0];
      const destination = routePoints[routePoints.length - 1];
      const waypoints = routePoints.slice(1, -1).map(point => ({
        location: { lat: point.lat, lng: point.lng },
        stopover: true
      }));

      const result = await new google.maps.DirectionsService().route({
        origin: { lat: origin.lat, lng: origin.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: true
      });

      setDirections(result);
      logInfo('Route calculated successfully');
    } catch (error) {
      setError('Failed to calculate route. Please try different points.');
      logError('Route calculation failed:', error);
    } finally {
      setCalculating(false);
    }
  }, [points]);

  const handleSaveRoute = async () => {
    if (!directions || !routeName || !activeSurvey) return;

    try {
      const route = directions.routes[0];
      const trackPoints = route.overview_path.map(point => ({
        latitude: point.lat(),
        longitude: point.lng(),
        timestamp: new Date().toISOString()
      }));

      if (editingRoute) {
        // Update existing route
        await updateDoc(doc(db, 'routes', editingRoute.id), {
          name: routeName,
          color: routeColor,
          trackPoints,
          distance: route.legs.reduce((total, leg) => total + leg.distance.value, 0),
          updatedAt: new Date().toISOString()
        });
      } else {
        // Create new route
        await addDoc(collection(db, 'routes'), {
          surveyId: activeSurvey.id,
          name: routeName,
          color: routeColor,
          trackPoints,
          distance: route.legs.reduce((total, leg) => total + leg.distance.value, 0),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }

      onClose();
    } catch (error) {
      logError('Failed to save route:', error);
      setError('Failed to save route');
    }
  };

  const handleCopyRouteName = () => {
    if (routeName) {
      navigator.clipboard.writeText(routeName);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div 
      className={`fixed top-20 right-4 z-[3000] bg-[#2A2B2E] rounded-lg shadow-lg flex flex-col overflow-hidden transition-all duration-200 ${
        isMaximized 
          ? 'w-[600px] h-[calc(100vh-120px)]' 
          : isCollapsed 
            ? 'w-80 h-12'
            : 'w-80 max-h-[calc(100vh-100px)]'
      } ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-2 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 hover:bg-gray-700 rounded-lg"
          >
            {isCollapsed ? <Plus className="w-4 h-4 text-white" /> : <X className="w-4 h-4 text-white" />}
          </button>
          <h3 className="text-sm font-medium text-white">Route Planner</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyRouteName}
            className="p-1 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white"
            disabled={!routeName}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setSelectionMode(!selectionMode)}
            className={`p-1 rounded-lg ${
              selectionMode ? 'bg-blue-500 text-white' : 'hover:bg-gray-700 text-gray-400'
            }`}
          >
            {selectionMode ? <Crosshair className="w-4 h-4" /> : <Hand className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setIsMaximized(!isMaximized)}
            className="p-1 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white"
          >
            {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {/* Instructions */}
            <div className="text-sm text-gray-400 bg-gray-700/50 p-3 rounded-lg">
              Click on the map to set points for your route. The first point will be the origin (green),
              intermediate points will be waypoints (orange), and the last point will be the destination (red).
            </div>

            {/* Point Details */}
            <div className="space-y-2">
              {points.map((point, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-700/50 p-2 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        index === 0 ? 'bg-green-500' :
                        index === points.length - 1 ? 'bg-red-500' :
                        'bg-yellow-500'
                      }`} />
                      <div className="text-xs text-gray-300 truncate">
                        {point.lat.toFixed(6)}, {point.lng.toFixed(6)}
                      </div>
                    </div>
                    {point.address && (
                      <div className="text-xs text-gray-500 truncate pl-4 mt-1">
                        {point.address}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setPoints(points.filter((_, i) => i !== index));
                      setDirections(null);
                    }}
                    className="p-1 hover:bg-red-500/20 text-red-400 rounded shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Route Options */}
            {points.length >= 2 && (
              <div className="space-y-4 border-t border-gray-700 pt-4">
                <div className="space-y-2">
                  <Input
                    label="Route Name"
                    value={routeName}
                    onChange={(e) => setRouteName(e.target.value)}
                    placeholder="Enter route name"
                    className="bg-gray-700 border-gray-600 text-white"
                    labelClassName="text-gray-300"
                  />

                  <div className="flex gap-2">
                    {['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'].map(color => (
                      <button
                        key={color}
                        onClick={() => setRouteColor(color)}
                        className={`w-8 h-8 rounded-full ${routeColor === color ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => calculateRoute()}
                    disabled={calculating}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    {calculating ? (
                      <>
                        <RotateCw className="w-4 h-4 animate-spin mr-2" />
                        Calculating...
                      </>
                    ) : (
                      <>
                        <Route className="w-4 h-4 mr-2" />
                        Calculate Route
                      </>
                    )}
                  </Button>
                  
                  {directions && (
                    <Button
                      onClick={handleSaveRoute}
                      variant="primary"
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                      disabled={!routeName || !activeSurvey}
                    >
                      {editingRoute ? 'Update Route' : 'Save Route'}
                    </Button>
                  )}
                </div>

                {directions && (
                  <div className="bg-gray-700/50 p-3 rounded-lg space-y-2 text-sm text-gray-300">
                    <div className="flex justify-between">
                      <span>Total Distance:</span>
                      <span>{(directions.routes[0].legs.reduce((total, leg) => total + leg.distance.value, 0) / 1000).toFixed(1)} km</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Estimated Time:</span>
                      <span>{Math.round(directions.routes[0].legs.reduce((total, leg) => total + leg.duration.value, 0) / 60)} mins</span>
                    </div>
                  </div>
                )}

                {!activeSurvey && (
                  <div className="bg-yellow-500/10 text-yellow-400 p-3 rounded-lg text-sm">
                    Start recording a survey to save routes permanently
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Map Markers */}
      {points.map((point, index) => (
        <RouteMarker
          key={index}
          position={{ lat: point.lat, lng: point.lng }}
          type={index === 0 ? 'origin' : index === points.length - 1 ? 'destination' : 'waypoint'}
        />
      ))}
    </div>
  );
};

export default RoutePlanner;